/**
 * InsightPreviewSlot
 *
 * Renders a behaviour slot for insight_preview experience.
 * Displays insight blocks with fullscreen expansion, scheduling,
 * Slack integration, and PDF download capabilities.
 */

import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence } from 'motion/react';
import { Maximize2 } from 'lucide-react';
import { toast } from '@strivelabs/ui-core';
import { log } from '@/lib/logger';
import type {
  BehaviourAgentExperienceAction,
  UIActionEvent,
  UIBlock,
  ContainerBlock,
  InsightCardBlock,
} from '@/types/ui-blocks';
import { dispatchBlock, isContainer, renderContainerBlock } from '../block-renderer-utils';
import { InsightCardBlockRenderer } from '../insight-card-block-renderer';
import { useChatMetadata } from '@/contexts/ChatMetadataContext';
import { useUpdateBehaviourActionState } from '@/hooks/useUpdateBehaviourActionState';
import { ScheduleReportOverlay } from '@/components/schedule/ScheduleReportOverlay';
import { SendToSlackOverlay } from '@/components/slack/SendToSlackOverlay';
import { useGeneratePdfFromBlocks } from '@/hooks/useGeneratePdfFromBlocks';
import {
  FullscreenSlotOverlay,
  SlotContainer,
  downloadPdfFromUrl,
  isInsightCardBlock,
} from './shared';

// Slot action icons
import scheduleIcon from '@/assets/slot/schedule.svg';
import slackLogo from '@/assets/slot/slack-logo.png';
import downloadIcon from '@/assets/slot/download.svg';

/**
 * Recursively render blocks within the slot
 */
const renderSlotBlocks = (
  blocks: (UIBlock | ContainerBlock | InsightCardBlock)[],
  onUIAction: (event: UIActionEvent) => void,
  readOnly?: boolean
): React.ReactNode[] => {
  return blocks.map((block, index) => {
    // Handle InsightCardBlock
    if (isInsightCardBlock(block)) {
      return (
        <div key={`insight-card-${index}`}>
          <InsightCardBlockRenderer
            block={block}
            onUIAction={onUIAction}
          />
        </div>
      );
    }

    const uiBlock = block as UIBlock | ContainerBlock;

    // Handle container blocks
    if (isContainer(uiBlock)) {
      return renderContainerBlock(
        uiBlock as ContainerBlock,
        (innerBlocks) => renderSlotBlocks(innerBlocks, onUIAction, readOnly),
        { includeWrapper: true, index }
      );
    }

    // Regular block
    return (
      <div key={`slot-block-${index}`}>
        {dispatchBlock({
          block: uiBlock,
          onUIAction,
          readOnly,
        })}
      </div>
    );
  });
};

export interface InsightPreviewSlotProps {
  action: BehaviourAgentExperienceAction;
  onUIAction: (event: UIActionEvent) => void;
  readOnly?: boolean;
  chatHistoryId?: string;
  behaviourActionState?: Record<string, unknown>;
}

export function InsightPreviewSlot({
  action,
  onUIAction,
  readOnly,
  chatHistoryId,
  behaviourActionState,
}: InsightPreviewSlotProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isScheduleDrawerOpen, setIsScheduleDrawerOpen] = useState(false);
  const [isSendToSlackDrawerOpen, setIsSendToSlackDrawerOpen] = useState(false);
  const [shouldAnimateTowardsIcon, setShouldAnimateTowardsIcon] = useState(false);
  const slotRef = useRef<HTMLDivElement>(null);
  const [chatRoot, setChatRoot] = useState<HTMLElement | null>(null);
  const blocks = action.data?.blocks;

  const { generatePdf, isGenerating: isGeneratingPdf } = useGeneratePdfFromBlocks();

  // Get workflow metadata from context
  const { workflowInstanceId, workflowExecutionId } = useChatMetadata();

  // Get initial scheduled and sent_to_slack states from behaviour_action_state map (keyed by action.id)
  const actionState = action.id && behaviourActionState
    ? behaviourActionState[action.id] as Record<string, unknown> | undefined
    : undefined;
  const initialScheduled = (actionState?.scheduled as boolean) ?? false;
  const initialSentToSlack = (actionState?.sent_to_slack as boolean) ?? false;
  const [localScheduledState, setLocalScheduledState] = useState(initialScheduled);
  const [localSentToSlackState, setLocalSentToSlackState] = useState(initialSentToSlack);

  // Schedule report mutation
  const scheduleMutation = useUpdateBehaviourActionState({
    onSuccess: () => {
      setLocalScheduledState(true);
      toast({
        title: 'Report scheduled successfully',
        variant: 'default'
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to schedule report',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive'
      });
    },
  });

  // Send to Slack mutation
  const sendToSlackMutation = useUpdateBehaviourActionState({
    onSuccess: () => {
      setLocalSentToSlackState(true);
      toast({
        title: 'Sent to Slack successfully',
        variant: 'default'
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to send to Slack',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive'
      });
    },
  });

  // Handle schedule report click
  const handleScheduleReport = async () => {
    if (!workflowInstanceId || !workflowExecutionId || !action.id) {
      toast({
        title: 'Cannot schedule report',
        description: 'Missing required IDs',
        variant: 'destructive'
      });
      return;
    }

    if (!chatHistoryId) {
      toast({
        title: 'Cannot schedule report',
        description: 'Message ID not available yet. Please wait for the message to complete.',
        variant: 'destructive'
      });
      return;
    }

    // Open the schedule drawer
    setIsScheduleDrawerOpen(true);
  };

  // Handle successful form submission
  const handleScheduleSuccess = () => {
    // Update behaviour action state to mark as scheduled
    if (workflowInstanceId && workflowExecutionId && chatHistoryId && action.id) {
      // Trigger animation towards reports icon
      setShouldAnimateTowardsIcon(true);

      // Update backend - local state will be updated in mutation's onSuccess
      scheduleMutation.mutate({
        workflowInstanceId,
        workflowExecutionId,
        chatHistoryId,
        behaviourActionStateId: action.id,
        state: { scheduled: true },
      });

      // Close the drawer to trigger the exit animation
      setTimeout(() => {
        setIsScheduleDrawerOpen(false);
      }, 100);
    } else {
      // If we can't update the backend, we should not show a full success state.
      // Log an error and just close the drawer without the success animation.
      log('error', 'Missing required IDs to update behaviour action state after scheduling.');
      setIsScheduleDrawerOpen(false);
    }
  };

  // Handle overlay exit animation completion
  const handleOverlayExitComplete = () => {
    if (shouldAnimateTowardsIcon) {
      // Reset animation flag
      setShouldAnimateTowardsIcon(false);

      // Dispatch custom event to show notification in NavMain
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('report-scheduled'));
      }, 100);
    }
  };

  // Handle send to slack click
  const handleSendToSlack = () => {
    if (!workflowExecutionId) {
      toast({
        title: 'Cannot send to Slack',
        description: 'Missing workflow execution ID',
        variant: 'destructive'
      });
      return;
    }

    // Open the send to slack drawer
    setIsSendToSlackDrawerOpen(true);
  };

  // Handle successful send to slack
  const handleSendToSlackSuccess = () => {
    // Update behaviour action state to mark as sent_to_slack
    if (workflowInstanceId && workflowExecutionId && chatHistoryId && action.id) {
      // Update backend - local state will be updated in mutation's onSuccess
      sendToSlackMutation.mutate({
        workflowInstanceId,
        workflowExecutionId,
        chatHistoryId,
        behaviourActionStateId: action.id,
        state: { sent_to_slack: true },
      });

      setIsSendToSlackDrawerOpen(false);
    } else {
      log('error', 'Missing required IDs to update behaviour action state after sending to Slack.');
      setIsSendToSlackDrawerOpen(false);
    }
  };

  // Handle download report click
  const handleDownloadReport = async () => {
    if (!blocks || blocks.length === 0) {
      toast({
        title: 'Cannot download report',
        description: 'No report content available',
        variant: 'destructive'
      });
      return;
    }

    try {
      const reportTitle = action.data?.title || 'Report';

      const pdfResult = await generatePdf({
        blocks,
        title: reportTitle
      });

      // Download the PDF from S3 signed URL
      await downloadPdfFromUrl(pdfResult.url, `${reportTitle.replace(/\s+/g, '_')}.pdf`);

      toast({
        title: 'Report downloaded successfully',
        variant: 'default'
      });
    } catch (error) {
      toast({
        title: 'Failed to download report',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        variant: 'destructive'
      });
    }
  };

  // Determine if schedule button should be disabled.
  // Only disable if already scheduled or mutation in-flight;
  // the missing-ID guards are handled via toasts inside handleScheduleReport.
  const isScheduleDisabled = localScheduledState || scheduleMutation.isPending;

  // Resolve the chat container on mount
  useEffect(() => {
    if (slotRef.current) {
      const root = slotRef.current.closest<HTMLElement>('[data-chat-root]');
      if (root) setChatRoot(root);
    }
  }, []);

  if (!blocks || !Array.isArray(blocks) || blocks.length === 0) {
    return null;
  }

  return (
    <>
      <SlotContainer slotRef={slotRef}>
        {/* Expand button - positioned at top right */}
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="absolute top-2.5 right-2.5 z-10 size-6 flex items-center justify-center rounded-md bg-white/60 border border-black/10 shadow-sm hover:bg-white/80 transition-colors"
          aria-label="Expand to fullscreen"
        >
          <Maximize2 className="size-3.5 text-foreground" />
        </button>

        {/* Slot content */}
        <div className="p-10 space-y-7">
          {renderSlotBlocks(blocks, onUIAction, readOnly)}
        </div>

        {/* Footer with CTAs */}

        <div className="relative border-t border-black/[0.04] h-12 flex items-center justify-between px-2">
          {/* Left side - Schedule report */}
          <button
            type="button"
            onClick={handleScheduleReport}
            disabled={isScheduleDisabled}
            className="flex items-center gap-2 px-2.5 py-1 rounded-lg bg-white border border-black/[0.08] text-sm text-foreground hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={localScheduledState ? "Already scheduled" : "Schedule report"}
            title={localScheduledState ? "Already scheduled" : "Schedule this report"}
          >
            <img src={scheduleIcon} alt="" className="size-[18px]" />
            <span className="font-normal tracking-[-0.28px]">Schedule report</span>
          </button>
          {/* Right side - Send to slack & Download report */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSendToSlack}
              className="flex items-center gap-2 px-2.5 py-1 rounded-lg bg-white border border-black/[0.08] text-sm text-foreground hover:bg-gray-50 transition-colors"
              aria-label={localSentToSlackState ? "Re-send to Slack" : "Send to Slack"}
            >
              <img src={slackLogo} alt="" className="size-[13px] object-contain" />
              <span className="font-normal tracking-[-0.28px]">
                {localSentToSlackState ? 'Re-send to Slack' : 'Send to Slack'}
              </span>
            </button>

            <button
              type="button"
              onClick={handleDownloadReport}
              disabled={isGeneratingPdf}
              className="flex items-center gap-2 px-2.5 py-1 rounded-lg bg-white border border-black/[0.08] text-sm text-foreground hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Download report"
            >
              <img src={downloadIcon} alt="" className="size-3.5" />
              <span className="font-normal tracking-[-0.28px]">
                {isGeneratingPdf ? 'Generating PDF...' : 'Download report'}
              </span>
            </button>
          </div>
        </div>
      </SlotContainer>

      {/* Fullscreen overlay — portalled into the chat container */}
      <AnimatePresence>
        {isExpanded && chatRoot && (
          <FullscreenSlotOverlay
            onClose={() => setIsExpanded(false)}
            portalTarget={chatRoot}
          >
            {renderSlotBlocks(blocks, onUIAction, readOnly)}
          </FullscreenSlotOverlay>
        )}
      </AnimatePresence>

      {/* Schedule Report Fullscreen Overlay */}
      <AnimatePresence mode="wait" onExitComplete={handleOverlayExitComplete}>
        {isScheduleDrawerOpen && (
          <ScheduleReportOverlay
            key="schedule-overlay"
            isOpen={isScheduleDrawerOpen}
            workflowExecutionId={workflowExecutionId || ''}
            blocks={blocks || []}
            title={action.data?.title}
            onClose={() => setIsScheduleDrawerOpen(false)}
            onSuccess={handleScheduleSuccess}
            animateTowardsIcon={shouldAnimateTowardsIcon}
          />
        )}
      </AnimatePresence>

      {/* Send to Slack Fullscreen Overlay */}
      <AnimatePresence mode="wait">
        {isSendToSlackDrawerOpen && (
          <SendToSlackOverlay
            key="slack-overlay"
            isOpen={isSendToSlackDrawerOpen}
            blocks={blocks || []}
            title={action.data?.title}
            onClose={() => setIsSendToSlackDrawerOpen(false)}
            onSuccess={handleSendToSlackSuccess}
          />
        )}
      </AnimatePresence>
    </>
  );
}
